import { Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle } from '@mui/material';
import { useSnackbar } from 'notistack';
import { useMutation, useQueryClient } from 'react-query';
import { CerebroTextButton } from '../../../shared/components';
import { useApi } from '../../../shared/context';
import { QueryKeys } from '../../../shared/hooks';
import { TermSheetFacility } from '../../../shared/models';
import { EnumUtils, NumberUtils } from '../../../shared/utils';
import { CerebroApiEndpoints } from '../../../shared/utils/api';
import { getResponseData } from '../../../shared/utils/axios';

interface FacilityDeleteDialogProps {
	dataRoomGuid: string | undefined;
	termSheetGuid: string | undefined;
	revisionGuid: string | undefined;
	facility: TermSheetFacility | null;
	close: () => void;
}

export const FacilityDeleteDialog: React.FC<FacilityDeleteDialogProps> = ({
	dataRoomGuid,
	termSheetGuid,
	revisionGuid,
	facility,
	close,
}) => {
	const api = useApi();
	const snackbar = useSnackbar();
	const client = useQueryClient();

	const onDeleteConfirmation = () => {
		if (facility !== null) deleteFacility(facility);
	};

	const { mutate: deleteFacility } = useMutation<void, unknown, TermSheetFacility>(
		facilityToDelete =>
			getResponseData(
				api.delete<void>(
					CerebroApiEndpoints.TermSheets.FacilitySpecific(
						dataRoomGuid,
						termSheetGuid,
						revisionGuid,
						facilityToDelete.guid
					)
				)
			),
		{
			onMutate: () => {
				close();
			},
			onSuccess: () => {
				client.invalidateQueries(QueryKeys.TermSheets.Revision(dataRoomGuid, termSheetGuid, revisionGuid));
				snackbar.enqueueSnackbar('Facility Deleted.', { variant: 'success' });
				close();
			},
			onError: () => {
				snackbar.enqueueSnackbar('There was a problem deleting the facility.', { variant: 'error' });
			},
		}
	);

	return (
		<Dialog open={facility !== null} onClose={close}>
			<DialogTitle>Confirm Delete Facility</DialogTitle>
			<DialogContent>
				<DialogContentText>
					Are you sure you want to delete {EnumUtils.GetFacilityTypeDescription(facility?.facilityType)} for{' '}
					{NumberUtils.FormatCurrency(facility?.highEndAmount)}?
				</DialogContentText>
			</DialogContent>
			<DialogActions>
				<CerebroTextButton onClick={close}>No</CerebroTextButton>
				<CerebroTextButton onClick={onDeleteConfirmation}>Yes</CerebroTextButton>
			</DialogActions>
		</Dialog>
	);
};
